"use client";

import { useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Icon, IconName } from "@perceptron/ui/components/icon";
import { Input } from "@perceptron/ui/components/input";
import { Label } from "@perceptron/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@perceptron/ui/components/popover";
import { SelectButton, SelectButtonLabel, SelectButtonValue } from "@perceptron/ui/components/selectButton";
import { Switch } from "@perceptron/ui/components/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@perceptron/ui/components/tooltip";
import { cn } from "@perceptron/utils/cn";

import { SelectOutputFormatContent } from "@demo/components/SelectOutputFormat/Content";
import { env } from "@demo/env";
import { useModelConfiguration, type ToolsLockReason } from "@demo/hooks/useModelConfiguration";
import { AVAILABLE_MODELS } from "@demo/shared/types";
import { useModelStore } from "@demo/store/model";

import ToggleButton from "../PromptControls/ToggleButton";

const FALLBACK_TEMPERATURE = 0.0;

/** The tooltip for a greyed-out Tools toggle, or `undefined` when it is usable. */
const describeToolsLock = (reason: ToolsLockReason): string | undefined => {
  switch (reason) {
    case "model":
      return strings.settings.tools.unavailable;
    case "media":
      return strings.settings.tools.imageOnlyPrompts;
    case "thinking":
      return strings.settings.tools.requiresThinking;
    default:
      return undefined;
  }
};

type ModelConfiguration = ReturnType<typeof useModelConfiguration>;

// Click-driven popover used on mobile, where Radix Tooltip's hover/focus
// trigger is unreliable on touch (the help-circle is an SVG and tapping
// does not focus it).
const SettingsTooltip = ({ content }: { content: string }) => (
  <Popover>
    <PopoverTrigger
      aria-label={strings.settings.ariaLabels.moreInfo}
      className="inline-flex size-4 items-center justify-center text-foreground-secondary"
    >
      <Icon className="size-4" name="help-circle" />
    </PopoverTrigger>
    <PopoverContent className="max-w-64 p-2 text-xs" side="top">
      {content}
    </PopoverContent>
  </Popover>
);

// --- Sub-Components ---

type MobileView = "main" | "format" | "model";

const DrilldownRow = ({
  label,
  value,
  valueIcon,
  onClick,
}: {
  label: string;
  value: string;
  valueIcon?: IconName;
  onClick: () => void;
}) => (
  <Button
    className="flex w-full items-center justify-between gap-6 rounded-md px-0 hover:bg-background-tint"
    type="button"
    variant="ghost"
    onClick={onClick}
  >
    <span className="whitespace-nowrap body">{label}</span>
    <div className="flex min-w-0 items-center gap-1">
      <div className="flex items-center gap-1 truncate rounded-md bg-background p-2">
        {valueIcon && <Icon className="size-4 shrink-0" name={valueIcon} />}
        <span className="truncate legal">{value}</span>
      </div>
      <Icon className="size-4 shrink-0" name="chevron-right" />
    </div>
  </Button>
);

const SubViewHeader = ({ title, onBack }: { title: string; onBack: () => void }) => (
  <div className="flex items-center gap-1 px-2 py-2">
    <Button aria-label={strings.settings.ariaLabels.back} className="mr-1 px-0" variant="ghost" onClick={onBack}>
      <Icon className="size-4" name="chevron-left" />
    </Button>
    <span className="legal">{title}</span>
  </div>
);

const ModelList = ({ selectedName, onSelect }: { selectedName: string; onSelect: (modelName: string) => void }) => (
  <div className="rounded-md bg-background-alt-2 p-1">
    {AVAILABLE_MODELS.map((model) => {
      if (env.NEXT_PUBLIC_APP_ENV === "production" && !model.isPublic) return null;
      if (model.name === "custom-model") return null;

      const isSelected = model.name === selectedName;
      return (
        <button
          className={cn(
            "flex w-full items-center justify-between rounded-md px-3 py-2 text-left body hover:bg-background-tint",
            isSelected && "bg-background-hover",
          )}
          key={model.name}
          type="button"
          onClick={() => onSelect(model.name)}
        >
          <span className="truncate">{model.publicName}</span>
        </button>
      );
    })}
  </div>
);

const MobileSettingsDropdown = ({ config }: { config: ModelConfiguration }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<MobileView>("main");

  const selectedModel = useModelStore((state) => state.selectedModel);
  const setSelectedModel = useModelStore((state) => state.setSelectedModel);
  const setCustomModelName = useModelStore((state) => state.setCustomModelName);

  const {
    activeMedia,
    canModelThink,
    isThinkingEnabled,
    isToolsEnabled,
    isToolsLocked,
    toolsLockReason,
    canModelHearAudio,
    isAudioLocked,
    isAudioEnabled,
    handleThinkingToggle,
    handleToolsToggle,
    handleAudioToggle,
    outputFormat,
  } = config;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    // Reset to main whenever the popover closes so reopening starts fresh.
    if (!open) setView("main");
  };

  const handleSelectModel = (modelName: string) => {
    const model = AVAILABLE_MODELS.find((m) => m.name === modelName);
    if (!model) return;
    setCustomModelName("");
    setSelectedModel(model);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          className="flex h-auto w-fit items-center gap-1 px-3 py-2 hover:bg-background-tint focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 data-[state=open]:bg-background-tint"
          disabled={!activeMedia}
          size="sm"
          variant="ghost"
        >
          <Icon className="h-4 w-4 opacity-80 sm:size-5" name="sliders-horizontal" />
          <span className="body">{strings.settings.buttonLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-w-64 bg-background p-0">
        {view === "main" && (
          <>
            <div className="px-3 py-2">
              <span className="legal">{strings.settings.buttonLabel}</span>
            </div>
            <div className="rounded-md bg-background-alt-2 p-3">
              {/* Thinking Toggle */}
              {canModelThink && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Label className="cursor-pointer body" htmlFor="thinking-toggle">
                      {strings.settings.thinking.label}
                    </Label>
                    <SettingsTooltip content={strings.settings.thinking.tooltip} />
                  </div>
                  <Switch
                    checked={isThinkingEnabled}
                    disabled={!canModelThink}
                    id="thinking-toggle"
                    onCheckedChange={handleThinkingToggle}
                  />
                </div>
              )}

              {/* Tools Toggle: always shown, greyed out with the reason in its tooltip */}
              <div className={cn("flex items-center justify-between", canModelThink && "mt-3")}>
                <div className="flex items-center gap-1">
                  <Label
                    className={cn("cursor-pointer body", isToolsLocked && "text-foreground-disabled")}
                    htmlFor="tools-toggle"
                  >
                    {strings.settings.tools.label}
                  </Label>
                  <SettingsTooltip content={describeToolsLock(toolsLockReason) ?? strings.settings.tools.tooltip} />
                </div>
                <Switch
                  checked={isToolsEnabled}
                  disabled={isToolsLocked}
                  id="tools-toggle"
                  onCheckedChange={handleToolsToggle}
                />
              </div>

              {/* Audio Toggle: only for a model that hears audio, greyed out without a video */}
              {canModelHearAudio && (
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Label
                      className={cn("cursor-pointer body", isAudioLocked && "text-foreground-disabled")}
                      htmlFor="audio-toggle"
                    >
                      {strings.settings.audio.label}
                    </Label>
                    <SettingsTooltip
                      content={isAudioLocked ? strings.settings.audio.requiresVideo : strings.settings.audio.tooltip}
                    />
                  </div>
                  <Switch
                    checked={isAudioEnabled && !isAudioLocked}
                    disabled={isAudioLocked}
                    id="audio-toggle"
                    onCheckedChange={handleAudioToggle}
                  />
                </div>
              )}

              <div className="mt-1" />

              {/* Drilldown rows */}
              <DrilldownRow
                label={strings.settings.outputLabel}
                value={outputFormat.label}
                valueIcon={outputFormat.icon as IconName}
                onClick={() => setView("format")}
              />
              <DrilldownRow
                label={strings.settings.modelLabel}
                value={selectedModel.publicName}
                onClick={() => setView("model")}
              />
            </div>
          </>
        )}

        {view === "format" && (
          <>
            <SubViewHeader title={strings.settings.outputLabel} onBack={() => setView("main")} />
            <SelectOutputFormatContent onClose={() => setIsOpen(false)} />
          </>
        )}

        {view === "model" && (
          <>
            <SubViewHeader title={strings.settings.modelLabel} onBack={() => setView("main")} />
            <ModelList selectedName={selectedModel.name} onSelect={handleSelectModel} />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

const DesktopSettings = ({ config, showLabels }: { config: ModelConfiguration; showLabels: boolean }) => {
  const [isFormatSelectorOpen, setIsFormatSelectorOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const selectedModel = useModelStore((state) => state.selectedModel);

  const {
    activeMedia,
    outputFormat,
    isDevMode,
    currentTemperature,
    setTemperature,
    canModelThink,
    isThinkingLocked,
    isThinkingEnabled,
    handleThinkingToggle,
    isToolsLocked,
    toolsLockReason,
    isToolsEnabled,
    handleToolsToggle,
    canModelHearAudio,
    isAudioLocked,
    isAudioEnabled,
    handleAudioToggle,
  } = config;

  // A disabled button gets no pointer events, so the tooltips listen on a wrapper.
  const toolsTooltip =
    describeToolsLock(toolsLockReason) ?? (showLabels ? strings.settings.tools.tooltip : strings.settings.tools.label);

  const handleFormatOpenChange = (open: boolean) => {
    if (!activeMedia) {
      setIsFormatSelectorOpen(false);
      return;
    }
    setIsFormatSelectorOpen(open);
  };

  return (
    <div className="flex items-center gap-1">
      {/* Dev/Config Settings (Temperature) */}
      {isDevMode && (
        <Popover open={isConfigOpen} onOpenChange={setIsConfigOpen}>
          <PopoverTrigger asChild>
            <Button
              className="flex aspect-square h-auto w-fit items-center gap-1 rounded-full p-2 hover:bg-background-tint focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 data-[state=open]:bg-background-tint"
              disabled={!activeMedia}
              size="sm"
              variant="ghost"
            >
              <Icon className="h-4 w-4 opacity-80 sm:size-5" name="settings" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="max-w-64 p-0">
            <div className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Label className="cursor-pointer body" htmlFor="temperature-input">
                    {strings.settings.temperature.label}
                  </Label>
                </div>

                <Input
                  className="w-fit p-0 pl-2 text-right"
                  id="temperature-input"
                  max={1}
                  min={0}
                  step={0.1}
                  type="number"
                  value={currentTemperature ?? selectedModel.defaultTemperature ?? FALLBACK_TEMPERATURE}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 1) {
                      setTemperature(value);
                    }
                  }}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Output Format Selector */}
      <Popover open={isFormatSelectorOpen} onOpenChange={handleFormatOpenChange}>
        <PopoverTrigger asChild>
          <SelectButton disabled={!activeMedia} role="menuitem">
            <SelectButtonLabel>{strings.settings.outputLabel}</SelectButtonLabel>
            <SelectButtonValue className="bg-background-hover" icon={outputFormat.icon as IconName}>
              {outputFormat.label}
            </SelectButtonValue>
          </SelectButton>
        </PopoverTrigger>
        <PopoverContent align="end" className="max-w-64 p-0" side="right">
          <div>
            <SelectOutputFormatContent onClose={() => setIsFormatSelectorOpen(false)} />
          </div>
        </PopoverContent>
      </Popover>

      {/* Thinking Button */}
      {canModelThink && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex" data-testid="thinking-toggle-wrapper">
              <ToggleButton
                animated="reverse"
                icon="thinking"
                isActive={isThinkingEnabled}
                isDisabled={isThinkingLocked}
                label={strings.settings.thinking.label}
                showLabel={showLabels}
                onClick={() => handleThinkingToggle(!isThinkingEnabled)}
              />
            </span>
          </TooltipTrigger>
          {/* Names the control when its label is gone, explains it when not. */}
          <TooltipContent>
            <p>{showLabels ? strings.settings.thinking.tooltip : strings.settings.thinking.label}</p>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Tools Button: always shown; greyed out with the reason in its tooltip */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" data-testid="tools-toggle-wrapper">
            <ToggleButton
              animated="finish"
              icon="tool-case"
              isActive={isToolsEnabled}
              isDisabled={isToolsLocked}
              label={strings.settings.tools.label}
              showLabel={showLabels}
              onClick={() => handleToolsToggle(!isToolsEnabled)}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{toolsTooltip}</p>
        </TooltipContent>
      </Tooltip>

      {/* Audio Button: only for a model that hears audio; greyed out without a video */}
      {canModelHearAudio && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex" data-testid="audio-toggle-wrapper">
              <ToggleButton
                animated="finish"
                icon="audio-lines"
                isActive={isAudioEnabled}
                isDisabled={isAudioLocked}
                label={strings.settings.audio.label}
                showLabel={showLabels}
                onClick={() => handleAudioToggle(!isAudioEnabled)}
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isAudioLocked
                ? strings.settings.audio.requiresVideo
                : showLabels
                  ? strings.settings.audio.tooltip
                  : strings.settings.audio.label}
            </p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

/**
 * Both variants render; `.settings-*` in globals.css picks between them, so the
 * server's markup is already right. Only the labels wait on a measurement.
 */
const Settings = ({ showLabels = true }: { showLabels?: boolean }) => {
  const config = useModelConfiguration();

  return (
    <>
      <div className="settings-compact">
        <MobileSettingsDropdown config={config} />
      </div>
      <div className="settings-full">
        <DesktopSettings config={config} showLabels={showLabels} />
      </div>
    </>
  );
};

export default Settings;
